import React, { useEffect, useState, useRef } from "react";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import InventoryTables from "./InventoryTables";
import api from "../Service/api";
import InventoryPrint from "./InventoryPrint";

const calculatePieceCount = (totalQuantity, weightPerPiece) => {
  if (!totalQuantity || !weightPerPiece || weightPerPiece === 0) return 0;
  const pieces = parseFloat(totalQuantity) / parseFloat(weightPerPiece);
  if (Math.abs(pieces - Math.round(pieces)) < 0.000001) {
    return Math.round(pieces);
  }
  return pieces; // return number (not string) for Excel
};

const InventoryPage = () => {
  const [inventory, setInventory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedLength, setSelectedLength] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [isPrinting, setIsPrinting] = useState(false);
  const [categoryInput, setCategoryInput] = useState("");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [productInput, setProductInput] = useState("");
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [sizeInput, setSizeInput] = useState("");
  const [showSizeDropdown, setShowSizeDropdown] = useState(false);
  const [monthInput, setMonthInput] = useState("");
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [productsForCategory, setProductsForCategory] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [availableLengths, setAvailableLengths] = useState([]);
  const [allAvailableSizes, setAllAvailableSizes] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  
  // Loading states
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingInventory, setLoadingInventory] = useState(false);
  
  const generateMonths = () => {
    const months = [];
    const today = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months.push(yearMonth);
    }
    return months;
  };
  
  const availableMonths = generateMonths();
  
  const formatMonthForDisplay = (yearMonth) => {
    if (!yearMonth) return "";
    const [year, month] = yearMonth.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };
  
  const categoryRef = useRef(null);
  const productRef = useRef(null);
  const sizeRef = useRef(null);
  const monthRef = useRef(null);
  const printRef = useRef();

  const handlePrint = async () => {
    if (!inventory || isPrinting) return;
    setIsPrinting(true);
    const printWindow = window.open('', '_blank');
    const printContent = printRef.current;
    const printHtml = printContent.outerHTML;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Inventory Report</title>
          <style>
            @media print {
              @page { size: A4; margin: 15mm 10mm; }
              body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
              table { width: 100%; border-collapse: collapse; }
              th, td { border: 1px solid #ddd; padding: 8px; }
              th { background-color: #f2f2f2; }
            }
          </style>
        </head>
        <body>${printHtml}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    setIsPrinting(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryRef.current && !categoryRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
      if (productRef.current && !productRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
      if (sizeRef.current && !sizeRef.current.contains(event.target)) {
        setShowSizeDropdown(false);
      }
      if (monthRef.current && !monthRef.current.contains(event.target)) {
        setShowMonthDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setLoadingCategories(true);
    api.get("/api/category/getCategories")
      .then(res => {
        if (res.data.code === 1) {
          const data = res.data.additionalDetail;
          if (Array.isArray(data)) {
            setCategories(data);
          } else if (data && data.categoryName) {
            setCategories([data]);
          } else {
            const cats = Object.keys(data).map(key => ({ categoryName: key }));
            setCategories(cats);
          }
        }
      })
      .catch(console.error)
      .finally(() => setLoadingCategories(false));
    
    setLoadingProducts(true);
    api.get("/product/names-prices")
      .then(res => {
        if (res.data.code === 1) {
          setAllProducts(res.data.additionalDetail || []);
        }
      })
      .catch(console.error)
      .finally(() => setLoadingProducts(false));
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      const products = allProducts.filter(p => 
        p.category && p.category.toLowerCase() === selectedCategory.toLowerCase()
      );
      setProductsForCategory(products);
    } else {
      setProductsForCategory([]);
    }
  }, [selectedCategory, allProducts]);

  // Extract all available sizes from inventory across all categories
  useEffect(() => {
    if (inventory) {
      const allSizes = new Set();
      Object.keys(inventory).forEach(category => {
        const categoryData = inventory[category];
        Object.keys(categoryData).forEach(product => {
          const productData = categoryData[product];
          // For roll, sizes are first level keys
          if (category.toLowerCase() === 'roll') {
            Object.keys(productData).forEach(size => {
              allSizes.add(size);
            });
          } else if (category.toLowerCase() === 'reel' || category.toLowerCase() === 'tota') {
            // For reel/tota, sizes are second level keys (after length)
            Object.values(productData).forEach(lengthData => {
              if (lengthData && typeof lengthData === 'object') {
                Object.keys(lengthData).forEach(size => {
                  allSizes.add(size);
                });
              }
            });
          }
        });
      });
      setAllAvailableSizes(Array.from(allSizes).sort((a, b) => {
        // Sort numerically if both are numbers
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.localeCompare(b);
      }));
    }
  }, [inventory]);

  // Extract available sizes/lengths from inventory for the selected category
  useEffect(() => {
    if (inventory && selectedCategory && selectedProduct) {
      const categoryData = inventory[selectedCategory.toLowerCase()];
      if (categoryData && categoryData[selectedProduct]) {
        const productData = categoryData[selectedProduct];
        const isReelOrTotaCat = selectedCategory.toLowerCase() === 'reel' || selectedCategory.toLowerCase() === 'tota';
        
        if (isReelOrTotaCat) {
          // For reel/tota: extract lengths (second level keys)
          const lengths = new Set();
          // Also extract sizes from the data
          const sizes = new Set();
          Object.values(productData).forEach(weightData => {
            Object.keys(weightData).forEach(key => {
              lengths.add(key);
            });
            // Also collect sizes from the weight data
            Object.values(weightData).forEach(sizeData => {
              if (sizeData && typeof sizeData === 'object') {
                Object.keys(sizeData).forEach(size => {
                  sizes.add(size);
                });
              }
            });
          });
          setAvailableLengths(Array.from(lengths).sort());
          setAvailableSizes(Array.from(sizes).sort((a, b) => {
            const numA = parseFloat(a);
            const numB = parseFloat(b);
            if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
            return a.localeCompare(b);
          }));
        } else if (selectedCategory.toLowerCase() === 'roll') {
          // For roll: extract sizes (first level keys)
          const sizes = new Set();
          Object.keys(productData).forEach(sizeKey => {
            sizes.add(sizeKey);
          });
          setAvailableSizes(Array.from(sizes).sort((a, b) => {
            const numA = parseFloat(a);
            const numB = parseFloat(b);
            if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
            return a.localeCompare(b);
          }));
          setAvailableLengths([]);
        } else {
          setAvailableSizes([]);
          setAvailableLengths([]);
        }
      } else {
        setAvailableSizes([]);
        setAvailableLengths([]);
      }
    } else {
      setAvailableSizes([]);
      setAvailableLengths([]);
    }
  }, [inventory, selectedCategory, selectedProduct]);

  // Fetch inventory when filters change
  useEffect(() => {
    const params = {};
    if (selectedCategory) params.category = selectedCategory;
    if (selectedProduct) params.productName = selectedProduct;
    if (selectedSize) params.size = selectedSize;
    if (selectedLength) params.length = selectedLength;
    
    console.log("🔍 Fetching inventory with params:", params);
    setLoadingInventory(true);
    
    api.get("/api/inventory/grouped-by-category", { params })
      .then(res => {
        console.log("📦 Response:", res.data);
        if (res.data.code === 1) {
          setInventory(res.data.additionalDetail);
        } else {
          setInventory(null);
        }
      })
      .catch(err => console.error("API Error:", err))
      .finally(() => setLoadingInventory(false));
  }, [selectedCategory, selectedProduct, selectedSize, selectedLength, selectedMonth]);

  const filteredCategories = categories.filter(cat =>
    cat.categoryName.toLowerCase().includes(categoryInput.toLowerCase())
  );

  const filteredProducts = productsForCategory.filter(prod =>
    prod.productName.toLowerCase().includes(productInput.toLowerCase())
  );

  const isReelOrTota = selectedCategory && (selectedCategory.toLowerCase() === 'reel' || selectedCategory.toLowerCase() === 'tota');
  const isRoll = selectedCategory && selectedCategory.toLowerCase() === 'roll';
  
  // For size dropdown, use all available sizes if no category is selected or show category-specific sizes
  const sizeDropdownItems = !selectedCategory ? allAvailableSizes : 
                           (isReelOrTota ? availableSizes : 
                           (isRoll ? availableSizes : []));
  
  const filteredSizeItems = sizeDropdownItems.filter(item =>
    item.toLowerCase().includes(sizeInput.toLowerCase())
  );
  
  // Length dropdown remains category-specific
  const filteredLengthItems = availableLengths.filter(item =>
    item.toLowerCase().includes(sizeInput.toLowerCase())
  );

  const filteredMonths = availableMonths.filter(month => {
    const displayMonth = formatMonthForDisplay(month);
    return displayMonth.toLowerCase().includes(monthInput.toLowerCase()) ||
           month.toLowerCase().includes(monthInput.toLowerCase());
  });

  const handleCategorySelect = (catName) => {
    setSelectedCategory(catName);
    setCategoryInput(catName);
    setShowCategoryDropdown(false);
    setSelectedProduct("");
    setProductInput("");
    setSelectedSize("");
    setSizeInput("");
    setSelectedLength("");
  };

  const handleProductSelect = (productName) => {
    setSelectedProduct(productName);
    setProductInput(productName);
    setShowProductDropdown(false);
    setSelectedSize("");
    setSizeInput("");
    setSelectedLength("");
  };

  const handleSizeSelect = (value) => {
    setSelectedSize(value);
    setSizeInput(value);
    setShowSizeDropdown(false);
    // Reset length when size is selected for reel/tota
    if (isReelOrTota) {
      setSelectedLength("");
    }
  };

  const handleLengthSelect = (value) => {
    setSelectedLength(value);
    setSizeInput(value);
    setShowSizeDropdown(false);
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    setMonthInput(formatMonthForDisplay(month));
    setShowMonthDropdown(false);
  };

  const clearFilters = () => {
    setSelectedCategory("");
    setCategoryInput("");
    setSelectedProduct("");
    setProductInput("");
    setSelectedSize("");
    setSelectedLength("");
    setSizeInput("");
    setSelectedMonth("");
    setMonthInput("");
  };

  const isInitialLoading = loadingCategories || loadingProducts;

  // Determine what filter type to show in the size dropdown
  const getFilterLabel = () => {
    if (!selectedCategory) return "Size (All Categories)";
    if (isReelOrTota) return "Size";
    if (isRoll) return "Roll Size";
    return "";
  };

     const exportToExcel = async () => {
    if (!inventory || Object.keys(inventory).length === 0) {
      alert("No inventory data to export.");
      return;
    }

    setIsExporting(true);
    try {
      // 1. Create the Workbook and Worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Inventory');

      // 2. Set column widths (A to H)
      worksheet.columns = [
        { width: 14 }, // A: Size
        { width: 14 }, // B: Weight/Ktae
        { width: 14 }, // C: Length/Gram
        { width: 20 }, // D: Vendor
        { width: 14 }, // E: Quantity
        { width: 14 }, // F: Pieces
        { width: 14 }, // G: Rate
        { width: 16 }, // H: Total Value
      ];

      // Use a standard row tracker for the whole export
      let currentRow = 1;

      // 3. Loop through the actual Inventory Data
      Object.entries(inventory).forEach(([categoryKey, categoryData]) => {
        const lowerCategory = categoryKey.toLowerCase();
        const isReelOrTota = lowerCategory === "reel" || lowerCategory === "tota";
        const isRoll = lowerCategory === "roll";
        const isPacket = lowerCategory === "packet";

        // --- [ CATEGORY GROUP HEADER ] ---
        worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
        const catRow = worksheet.getRow(currentRow);
        catRow.height = 25;
        catRow.getCell(1).value = categoryKey; // Category Name
        catRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E7E34' } }; // Dark Green (matching UI)
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        });
        currentRow++;

        // --- [ LOOP THROUGH PRODUCTS ] ---
        Object.entries(categoryData).forEach(([productName, productData]) => {
          if (!productData || Object.keys(productData).length === 0) return;

          // ---- [ PRODUCT GROUP HEADER ] ----
          worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
          const prodRow = worksheet.getRow(currentRow);
          prodRow.height = 22;
          prodRow.getCell(1).value = productName; // Product Name
          prodRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FF000000' }, size: 11 };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } }; // Light Green (matching UI)
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
          });
          currentRow++;

          // ---- [ COLUMN HEADERS ] ----
          const headerLabels = ['Size', 'Weight / Ktae', 'Length / Gram', 'Vendor', 'Quantity', 'Pieces', 'Rate', 'Total Value'];
          const headerRow = worksheet.addRow(headerLabels);
          headerRow.height = 20;
          headerRow.eachCell((cell) => {
            cell.font = { bold: true, size: 10 };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } }; // Light Grey
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          });
          currentRow++;

          // ---- [ GENERATE DATA ROWS ] ----
          let productTotalValue = 0;
          const rowsToWrite = [];

          // 1. Reel / Tota Logic
          if (isReelOrTota) {
            Object.entries(productData).forEach(([weight, weightData]) => {
              Object.entries(weightData).forEach(([length, lengthData]) => {
                Object.entries(lengthData).forEach(([vendor, vendorData]) => {
                  const qty = parseFloat(vendorData.quantity) || 0;
                  const pieceCount = calculatePieceCount(qty, parseFloat(weight));
                  const rate = parseFloat(vendorData.rate) || 0;
                  const total = parseFloat(vendorData.totalPrice) || 0;
                  rowsToWrite.push([length, weight, "", vendor, qty, pieceCount, rate, total]); // Column mapping
                  productTotalValue += total;
                });
              });
            });
          } 
          // 2. Roll Logic
          else if (isRoll) {
            Object.entries(productData).forEach(([size, sizeData]) => {
              if (sizeData.quantity !== undefined) {
                const qty = parseFloat(sizeData.quantity) || 0;
                const rate = parseFloat(sizeData.rate) || 0;
                const total = parseFloat(sizeData.totalPrice) || 0;
                rowsToWrite.push([size, "", "", "", qty, "", rate, total]);
                productTotalValue += total;
              } else {
                Object.entries(sizeData).forEach(([vendor, vendorData]) => {
                  const qty = parseFloat(vendorData.quantity) || 0;
                  const rate = parseFloat(vendorData.rate) || 0;
                  const total = parseFloat(vendorData.totalPrice) || 0;
                  rowsToWrite.push([size, "", "", vendor, qty, "", rate, total]);
                  productTotalValue += total;
                });
              }
            });
          } 
          // 3. Packet Logic
          else if (isPacket) {
            Object.entries(productData).forEach(([variantKey, variantData]) => {
              const [size, ktae, gram] = variantKey.split("|");
              const qty = parseFloat(variantData.quantity) || 0;
              const rate = parseFloat(variantData.rate) || 0;
              const total = parseFloat(variantData.totalPrice) || 0;
              rowsToWrite.push([size, ktae, gram, "", qty, "", rate, total]);
              productTotalValue += total;
            });
          } 
          // 4. Generic/Other Logic
          else {
            const qty = parseFloat(productData.quantity) || 0;
            const rate = parseFloat(productData.rate) || 0;
            const total = parseFloat(productData.totalPrice) || 0;
            rowsToWrite.push(["", "", "", "", qty, "", rate, total]);
            productTotalValue += total;
          }

          // ---- [ WRITE DATA TO WORKSHEET ] ----
          rowsToWrite.forEach(rowData => {
            const newRow = worksheet.addRow(rowData);
            newRow.eachCell((cell, colNumber) => {
              cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
              cell.alignment = { horizontal: 'center', vertical: 'middle' };
              
              // Format numbers in columns 5, 6, 7, 8 (Quantity, Pieces, Rate, Total)
              if (colNumber === 5 || colNumber === 6) {
                cell.numFmt = '#,##0';
              } else if (colNumber === 7 || colNumber === 8) {
                cell.numFmt = '#,##0.00';
              }
            });
            currentRow++;
          });

          // ---- [ SUBTOTAL ROW ] ----
          worksheet.mergeCells(`A${currentRow}:E${currentRow}`);
          const totalRow = worksheet.getRow(currentRow);
          totalRow.getCell(1).value = `Total for ${productName}:`;
          totalRow.getCell(8).value = productTotalValue;
          totalRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true, size: 10 };
            cell.alignment = { horizontal: colNumber === 8 ? 'center' : 'right', vertical: 'middle' };
            if (colNumber === 8) cell.numFmt = '#,##0.00';
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9F9F9' } }; // Very light grey for total
          });
          currentRow++;
        });
        // Add a blank row between categories for spacing
        currentRow++;
      });

      // 4. Download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `inventory_report_${new Date().toISOString().slice(0,10)}.xlsx`;
      
      saveAs(blob, fileName);

    } catch (error) {
      console.error("Export error:", error);
      alert("Failed to export Excel. Check console.");
    } finally {
      setIsExporting(false);
    }
  };
  return (
    <div className="purchase-container">
      <h2 className="page-title">Inventory Overview</h2>

      {isInitialLoading && (
        <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#e3f2fd', marginBottom: '10px', borderRadius: '4px' }}>
          ⏳ Loading data... {loadingCategories && "Categories "} {loadingProducts && "Products "}
        </div>
      )}

      <div style={{ marginBottom: "20px", display: "flex", gap: "15px", flexWrap: "wrap", alignItems: "flex-end" }}>
        
        {/* Category Search */}
        <div style={{ position: "relative", minWidth: "200px" }} ref={categoryRef}>
          <label style={{ fontSize: "14px", fontWeight: "500", display: "block", marginBottom: "4px" }}>Category:</label>
          <input
            type="text"
            value={categoryInput}
            onChange={(e) => {
              setCategoryInput(e.target.value);
              setShowCategoryDropdown(true);
              if (e.target.value === "") setSelectedCategory("");
            }}
            onFocus={() => setShowCategoryDropdown(true)}
            placeholder="Type to search..."
            disabled={loadingCategories}
            style={{ padding: "8px", width: "100%", border: "1px solid #ccc", borderRadius: "4px", fontSize: "14px" }}
          />
          {showCategoryDropdown && filteredCategories.length > 0 && !loadingCategories && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, maxHeight: "250px", overflowY: "auto", backgroundColor: "white", border: "1px solid #ccc", borderRadius: "4px", zIndex: 1000, boxShadow: "0 2px 8px rgba(0,0,0,0.15)", marginTop: "2px" }}>
              {filteredCategories.map((cat, idx) => (
                <div key={idx} style={{ padding: "10px", cursor: "pointer", borderBottom: "1px solid #eee", backgroundColor: selectedCategory === cat.categoryName ? "#e8f5e9" : "white" }}
                  onMouseDown={(e) => { e.preventDefault(); handleCategorySelect(cat.categoryName); }}
                >
                  {cat.categoryName}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Product Search */}
        {selectedCategory && (
          <div style={{ position: "relative", minWidth: "200px" }} ref={productRef}>
            <label style={{ fontSize: "14px", fontWeight: "500", display: "block", marginBottom: "4px" }}>Product:</label>
            <input
              type="text"
              value={productInput}
              onChange={(e) => {
                setProductInput(e.target.value);
                setShowProductDropdown(true);
                if (e.target.value === "") setSelectedProduct("");
              }}
              onFocus={() => setShowProductDropdown(true)}
              placeholder="Type to search..."
              style={{ padding: "8px", width: "100%", border: "1px solid #ccc", borderRadius: "4px", fontSize: "14px" }}
            />
            {showProductDropdown && filteredProducts.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, maxHeight: "250px", overflowY: "auto", backgroundColor: "white", border: "1px solid #ccc", borderRadius: "4px", zIndex: 1000, marginTop: "2px" }}>
                {filteredProducts.map((prod, idx) => (
                  <div key={idx} style={{ padding: "10px", cursor: "pointer", borderBottom: "1px solid #eee", backgroundColor: selectedProduct === prod.productName ? "#e8f5e9" : "white" }}
                    onMouseDown={(e) => { e.preventDefault(); handleProductSelect(prod.productName); }}
                  >
                    <div style={{ fontWeight: "500" }}>{prod.productName}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Size Filter - Now shows all sizes across categories when no category selected */}
        {(allAvailableSizes.length > 0 || (selectedCategory && (isReelOrTota || isRoll))) && (
          <div style={{ position: "relative", minWidth: "150px" }} ref={sizeRef}>
            <label style={{ fontSize: "14px", fontWeight: "500", display: "block", marginBottom: "4px" }}>
              {getFilterLabel() || "Size"}
            </label>
            <input
              type="text"
              value={sizeInput}
              onChange={(e) => {
                setSizeInput(e.target.value);
                setShowSizeDropdown(true);
                if (e.target.value === "") {
                  setSelectedSize("");
                }
              }}
              onFocus={() => setShowSizeDropdown(true)}
              placeholder={!selectedCategory ? "Filter by size..." : (isReelOrTota ? "Select size..." : "Filter by size...")}
              style={{ padding: "8px", width: "100%", border: "1px solid #ccc", borderRadius: "4px", fontSize: "14px" }}
            />
            {showSizeDropdown && filteredSizeItems.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, maxHeight: "200px", overflowY: "auto", backgroundColor: "white", border: "1px solid #ccc", borderRadius: "4px", zIndex: 1000, marginTop: "2px" }}>
                <div style={{ padding: "10px", cursor: "pointer", borderBottom: "1px solid #eee", backgroundColor: !selectedSize ? "#e8f5e9" : "white" }}
                  onMouseDown={(e) => { e.preventDefault(); handleSizeSelect(""); }}
                >
                  All
                </div>
                {filteredSizeItems.map((item, idx) => (
                  <div key={idx} style={{ padding: "10px", cursor: "pointer", borderBottom: "1px solid #eee", backgroundColor: selectedSize === item ? "#e8f5e9" : "white" }}
                    onMouseDown={(e) => { e.preventDefault(); handleSizeSelect(item); }}
                  >
                    {item}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Length Filter - Only for reel/tota when product is selected */}
        {selectedCategory && selectedProduct && isReelOrTota && availableLengths.length > 0 && (
          <div style={{ position: "relative", minWidth: "150px" }} ref={sizeRef}>
            <label style={{ fontSize: "14px", fontWeight: "500", display: "block", marginBottom: "4px" }}>
              Length
            </label>
            <input
              type="text"
              value={sizeInput}
              onChange={(e) => {
                setSizeInput(e.target.value);
                setShowSizeDropdown(true);
                if (e.target.value === "") {
                  setSelectedLength("");
                }
              }}
              onFocus={() => setShowSizeDropdown(true)}
              placeholder="Select length..."
              style={{ padding: "8px", width: "100%", border: "1px solid #ccc", borderRadius: "4px", fontSize: "14px" }}
            />
            {showSizeDropdown && filteredLengthItems.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, maxHeight: "200px", overflowY: "auto", backgroundColor: "white", border: "1px solid #ccc", borderRadius: "4px", zIndex: 1000, marginTop: "2px" }}>
                <div style={{ padding: "10px", cursor: "pointer", borderBottom: "1px solid #eee", backgroundColor: !selectedLength ? "#e8f5e9" : "white" }}
                  onMouseDown={(e) => { e.preventDefault(); handleLengthSelect(""); }}
                >
                  All
                </div>
                {filteredLengthItems.map((item, idx) => (
                  <div key={idx} style={{ padding: "10px", cursor: "pointer", borderBottom: "1px solid #eee", backgroundColor: selectedLength === item ? "#e8f5e9" : "white" }}
                    onMouseDown={(e) => { e.preventDefault(); handleLengthSelect(item); }}
                  >
                    {item}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Clear & Print Buttons */}
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
          {(selectedCategory || selectedProduct || selectedSize || selectedLength || selectedMonth) && (
            <button onClick={clearFilters} disabled={loadingInventory} style={{ padding: "8px 16px", cursor: loadingInventory ? "not-allowed" : "pointer", backgroundColor: "#dc3545", color: "white", border: "none", borderRadius: "4px" }}>
              Clear Filters
            </button>
          )}
          <button onClick={handlePrint} disabled={!inventory || isPrinting || loadingInventory} style={{ padding: "8px 16px", cursor: (!inventory || isPrinting || loadingInventory) ? "not-allowed" : "pointer", backgroundColor: "#0a8938", color: "white", border: "none", borderRadius: "4px" }}>
            🖨 {isPrinting ? "Preparing..." : loadingInventory ? "Loading..." : "Print Inventory"}
          </button>

          <button
          onClick={exportToExcel}
          disabled={!inventory || isExporting || loadingInventory}
          style={{
            padding: "8px 16px",
            cursor: (!inventory || isExporting || loadingInventory) ? "not-allowed" : "pointer",
            backgroundColor: "#217346",
            color: "white",
            border: "none",
            borderRadius: "4px",
          }}
        >
          📊 {isExporting ? "Exporting..." : loadingInventory ? "Loading..." : "Export to Excel"}
        </button>
        </div>
      </div>

      {/* Selected Filters Display */}
      {(selectedCategory || selectedProduct || selectedSize || selectedLength || selectedMonth) && (
        <div style={{ marginBottom: "12px", fontSize: "13px", color: "#555", fontStyle: "italic" }}>
          Filters Applied:
          {selectedCategory && ` Category: ${selectedCategory}`}
          {selectedProduct && ` | Product: ${selectedProduct}`}
          {selectedSize && ` | Size: ${selectedSize}`}
          {selectedLength && ` | Length: ${selectedLength}`}
          {selectedMonth && ` | Month: ${formatMonthForDisplay(selectedMonth)}`}
        </div>
      )}

      {/* Loading indicator */}
      {loadingInventory && (
        <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '4px', marginBottom: '10px' }}>
          📊 Loading inventory data...
        </div>
      )}

      {/* Inventory Table Component */}
      <InventoryTables data={inventory} selectedFilter={selectedSize || selectedLength} filterType={isRoll ? 'size' : 'length'} />

      {/* Hidden print layout */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          <InventoryPrint inventory={inventory} selectedCategory={selectedCategory} selectedProduct={selectedProduct} selectedMonth={selectedMonth} />
        </div>
      </div>
    </div>
  );
};

export default InventoryPage;